import { useEffect, useState, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  formatDateAPIFormat,
  convertBudgetToNumber,
  splitBudgetIntoValueAndUnit,
  parseDateString,
} from "../../hooks/commonHooks";
import { usePropertyStore } from "../../store/propertyStore";

const DEFAULT_VALUES = {
  name: "",
  city: "",
  area: "",
  state: "",
  country: "India",
  pincode: "",
  site_property_url: "",
  property_type: "",
  possession_status: "ongoing",
  unit_type: [],
  min_budget: "",
  max_budget: "",
  expected_delivery: "",
  status: "active",
  brochure: null,
  image: null,
};

const isValidPincode = (v) => /^\d{6}$/.test(v);
const isValidUrl = (v) => {
  try {
    new URL(v);
    return true;
  } catch {
    return false;
  }
};

// ── Field rules are static objects — defined once at module level ──────────
// Cross-field rules (max_budget, unit_type, etc.) are handled via trigger()
// in the hook, not inline, to avoid re-creating rule objects on every render.

const STATIC_RULES = {
  name: { required: "Property name is required" },
  city: { required: "City required" },
  area: { required: "Area required" },
  state: { required: "State required" },
  country: { required: "Country required" },
  possession_status: { required: "Possession status required" },
  property_type: { required: "Property type required" },
  min_budget: { required: "Min budget required" },
  max_budget: { required: "Max budget required" },
  expected_delivery: { required: "Required" },
  pincode: {
    required: "Pincode required",
    validate: (v) => isValidPincode(v) || "Invalid pincode (must be 6 digits)",
  },
  site_property_url: {
    validate: (v) => !v || isValidUrl(v) || "Invalid URL",
  },
  // unit_type, image, brochure, max_budget cross-validation → via trigger()
};

export const usePropertyForm = (initialData = {}, onSubmit, onCancel) => {
  const extractedPdfData = usePropertyStore((s) => s.extractedPdfData);
  const setExtractedPdfData = usePropertyStore((s) => s.setExtractedPdfData);
  const isEditMode = !!initialData?.id;

  const [minBudgetUnitType, setMinBudgetUnitType] = useState("");
  const [maxBudgetUnitType, setMaxBudgetUnitType] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    trigger,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });

  // ✅ useWatch — subscribes granularly, doesn't return new object every render
  const minBudget = useWatch({ control, name: "min_budget" });
  const maxBudget = useWatch({ control, name: "max_budget" });
  const brochure = useWatch({ control, name: "brochure" });
  const image = useWatch({ control, name: "image" });
  const propertyType = useWatch({ control, name: "property_type" });
  const unitType = useWatch({ control, name: "unit_type" });
  const possessionStatus = useWatch({ control, name: "possession_status" });
  const expectedDelivery = useWatch({ control, name: "expected_delivery" });
  const pincode = useWatch({ control, name: "pincode" });
  const name = useWatch({ control, name: "name" });
  const city = useWatch({ control, name: "city" });
  const area = useWatch({ control, name: "area" });
  const state = useWatch({ control, name: "state" });
  const country = useWatch({ control, name: "country" });

  // ── Load edit data only when ID changes ───────────────────────────────
  useEffect(() => {
    if (!initialData?.id) return;
    const min = splitBudgetIntoValueAndUnit(initialData.min_budget);
    const max = splitBudgetIntoValueAndUnit(initialData.max_budget);
    setMinBudgetUnitType(min.unit);
    setMaxBudgetUnitType(max.unit);
    if (initialData.details) {
      setExtractedPdfData(initialData.details);
    }
    reset({
      name: initialData.title || "",
      city: initialData.city || "",
      area: initialData.area || "",
      state: initialData.state || "",
      country: initialData.country || "",
      pincode: initialData.pincode || "",
      site_property_url: initialData.property_url || "",
      property_type: initialData.property_type || "",
      possession_status: initialData.possession_status || "ongoing",
      unit_type: Array.isArray(initialData.unit_type)
        ? initialData.unit_type
        : [],
      min_budget: min.value || "",
      max_budget: max.value || "",
      expected_delivery: parseDateString(initialData.expected_delivery) || "",
      status: initialData.status || "active",
      brochure: initialData.brochure_url || initialData.brochure || null,
      image: initialData.image_url || initialData.image || null,
    });
  }, [initialData?.id, reset]);

  // ── Cross-field validation via trigger (not inline rules) ─────────────
  useEffect(() => {
    if (maxBudget) trigger("max_budget");
  }, [minBudget, minBudgetUnitType, maxBudgetUnitType]);

  useEffect(() => {
    if (brochure) trigger("brochure");
  }, [extractedPdfData]);

  // ── isValidated — computed from watched values directly ───────────────
  // Don't rely on RHF's isValid (requires all fields touched first).
  // Instead derive it ourselves from the watched primitives above.
  const isValidated = (() => {
    if (!name || !city || !area || !state || !country) return false;
    if (!possessionStatus || !propertyType) return false;
    if (!minBudget || !maxBudget || !minBudgetUnitType || !maxBudgetUnitType)
      return false;
    if (!expectedDelivery) return false;
    if (!pincode || !isValidPincode(pincode)) return false;
    if (propertyType !== "plot" && !unitType?.length) return false;
    if (brochure && !extractedPdfData) return false;
    if (!image && !initialData?.image && !initialData?.image_url) return false;
    if (minBudget && maxBudget && minBudgetUnitType && maxBudgetUnitType) {
      const min = convertBudgetToNumber(minBudget, minBudgetUnitType);
      const max = convertBudgetToNumber(maxBudget, maxBudgetUnitType);
      if (max <= min) return false;
    }
    return true;
  })();

  // ── Submit ────────────────────────────────────────────────────────────
  const onFormSubmit = handleSubmit((data) => {
    // Extra guard for unit types (not RHF-registered fields)

    console.log("data", data);

    console.log("data.unit_type", data.unit_type);

    if (!minBudgetUnitType) {
      setError("min_budget", {
        type: "manual",
        message: "Min budget unit required",
      });
      return;
    }
    if (!maxBudgetUnitType) {
      setError("max_budget", {
        type: "manual",
        message: "Max budget unit required",
      });
      return;
    }
    if (data.property_type !== "plot" && !data.unit_type?.length) {
      setError("unit_type", { type: "manual", message: "Unit type required" });
      return;
    }
    if (!data.image && !initialData?.image && !initialData?.image_url) {
      setError("image", { type: "manual", message: "Image required" });
      return;
    }
    if (data.brochure && !extractedPdfData) {
      setError("brochure", {
        type: "manual",
        message: "Extract PDF data before submitting",
      });
      return;
    }

    const formDataObj = new FormData();
    Object.entries({
      title: data.name,
      city: data.city,
      area: data.area,
      state: data.state,
      country: data.country,
      pincode: data.pincode,
      property_url: data.site_property_url,
      possession_status: data.possession_status,
      property_type: data.property_type,
      min_budget: convertBudgetToNumber(data.min_budget, minBudgetUnitType),
      max_budget: convertBudgetToNumber(data.max_budget, maxBudgetUnitType),
      expected_delivery: formatDateAPIFormat(data.expected_delivery),
      status: data.status,
      location: data.area,
    }).forEach(([k, v]) => formDataObj.append(k, v));

    if (data.brochure && extractedPdfData)
      formDataObj.append("details", JSON.stringify(extractedPdfData));

    if (data.property_type !== "plot")
      data.unit_type.forEach((u) => formDataObj.append("unit_type", u));
    else formDataObj.append("unit_type", "");

    if (data.brochure instanceof File)
      formDataObj.append("brochure", data.brochure);
    if (data.image instanceof File) formDataObj.append("image", data.image);

    console.log(
      "Submitting form data:",
      Object.fromEntries(formDataObj.entries()),
    );

    onSubmit(formDataObj);
  });

  // ── Cancel ────────────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    reset(DEFAULT_VALUES);
    setMinBudgetUnitType("");
    setMaxBudgetUnitType("");
    setShowCalendar(false);
    onCancel();
  }, [reset, onCancel]);

  return {
    register,
    STATIC_RULES, // ✅ export the static rules object, not a function
    control,
    errors,
    brochure,
    expectedDelivery,
    propertyType,
    showCalendar,
    setShowCalendar,
    onFormSubmit,
    handleCancel,
    isEditMode,
    minBudgetUnitType,
    setMinBudgetUnitType,
    maxBudgetUnitType,
    setMaxBudgetUnitType,
    isValidated,
  };
};
